import { mergeClasses } from '@fluentui/react-components';
import { useState, useMemo } from 'react';
import {
  SearchBox,
  Card,
  Badge,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  Subtitle2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwise20Regular } from '@fluentui/react-icons';
import { AscoraJobSummary } from '../types';

interface Props {
  jobs: AscoraJobSummary[];
  loading: boolean;
  error: string | null;
  onSelectJob: (job: AscoraJobSummary) => void;
  onRefresh: () => void;
}

const useStyles = makeStyles({
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: '16px',
    paddingBottom: '20px',
    paddingLeft: '18px',
    paddingRight: '18px',
  },
  searchRow: {
    marginBottom: '14px',
  },
  sectionLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#394456',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    marginTop: '16px',
    marginBottom: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    minHeight: '26px',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '10px',
    paddingRight: '10px',
    borderRadius: '999px',
    backgroundColor: 'rgba(255,255,255,0.92)',
    boxShadow: 'inset 0 0 0 1px rgba(17,24,36,0.08), 0 8px 18px rgba(17,24,36,0.05)',
  },
  jobCard: {
    cursor: 'pointer',
    marginBottom: '12px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderLeftWidth: '0',
    borderLeftStyle: 'solid',
    borderLeftColor: '#E3272D',
    minHeight: '92px',
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    background: tokens.colorNeutralBackground1,
    transitionProperty: 'transform',
    transitionDuration: '0.08s',
    ':active': { transform: 'scale(0.98)' },
  },
  jobCardDone: {
    opacity: 0.9,
  },
  jobNum: {
    fontSize: '12px',
    color: '#C21F24',
    fontWeight: 600,
    letterSpacing: '0.45px',
    textTransform: 'uppercase',
  },
  jobTitle: {
    fontSize: '17px',
    fontWeight: 650,
    marginTop: '6px',
    lineHeight: 1.2,
  },
  jobDetail: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginTop: '6px',
    lineHeight: 1.35,
  },
  badgeRow: {
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
  },
  empty: {
    textAlign: 'center',
    paddingTop: '40px',
    paddingBottom: '40px',
    paddingLeft: '20px',
    paddingRight: '20px',
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '32px',
    paddingBottom: '32px',
  },
});

function formatCompletedDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '';
  }
}

export function JobsListScreen({ jobs, loading, error, onSelectJob, onRefresh }: Props) {
  const styles = useStyles();
  const [searchText, setSearchText] = useState('');

  const { needT5, completed } = useMemo(() => {
    const f = searchText.toLowerCase().trim();
    const filtered = jobs.filter(j =>
      !f ||
      (j.jobNumber || '').toLowerCase().includes(f) ||
      (j.jobName || '').toLowerCase().includes(f) ||
      (j.addressLine1 || '').toLowerCase().includes(f) ||
      (j.suburb || '').toLowerCase().includes(f) ||
      (j.siteCustomer?.name || '').toLowerCase().includes(f) ||
      (j.billingCustomer?.name || '').toLowerCase().includes(f)
    );
    return {
      needT5: filtered.filter(j => j.takeFiveStatus !== 'Completed'),
      completed: filtered.filter(j => j.takeFiveStatus === 'Completed'),
    };
  }, [jobs, searchText]);

  return (
    <div className={styles.body} data-screen-body>
      <div className={styles.searchRow}>
        <SearchBox
          placeholder="Search job number, site, client..."
          value={searchText}
          size="large"
          onChange={(_, data) => setSearchText(data.value)}
          style={{ width: '100%', borderRadius: 22, boxShadow: '0 10px 22px rgba(17,24,36,0.06)' }}
          input={{ style: { fontSize: '16px' } }}
        />
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Couldn't load jobs</MessageBarTitle>
            {error}
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="transparent" onClick={onRefresh}>Retry</Button>
          </MessageBarActions>
        </MessageBar>
      )}

      {loading && (
        <div className={styles.loading}>
          <Spinner label="Loading jobs from Ascora..." />
        </div>
      )}

      {!loading && !error && needT5.length === 0 && completed.length === 0 && (
        <div className={styles.empty}>
          No open jobs assigned to you.
          <Button appearance="outline" icon={<ArrowClockwise20Regular />} onClick={onRefresh}>
            Refresh
          </Button>
        </div>
      )}

      {needT5.length > 0 && (
        <>
          <Subtitle2 className={styles.sectionLabel}>Needs Take 5 before start</Subtitle2>
          {needT5.map(j => (
            <JobCard
              key={j.jobId}
              job={j}
              styles={styles}
              onClick={() => onSelectJob(j)}
            />
          ))}
        </>
      )}

      {completed.length > 0 && (
        <>
          <Subtitle2 className={styles.sectionLabel}>Completed</Subtitle2>
          {completed.map(j => (
            <JobCard
              key={j.jobId}
              job={j}
              styles={styles}
              onClick={() => onSelectJob(j)}
            />
          ))}
        </>
      )}
    </div>
  );
}

interface JobCardProps {
  job: AscoraJobSummary;
  onClick: () => void;
  styles: ReturnType<typeof useStyles>;
}

function JobCard({ job, onClick, styles }: JobCardProps) {
  const done = job.takeFiveStatus === 'Completed';
  return (
    <Card
      className={mergeClasses(styles.jobCard, done && styles.jobCardDone)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <div className={styles.jobNum}>{job.jobNumber}</div>
      <div className={styles.jobTitle}>{(job.jobName || '').trim() || 'Untitled job'}</div>
      <div className={styles.jobDetail}>{job.addressLine1}, {job.suburb} {job.postcode}</div>
      <div className={styles.jobDetail}>{job.siteCustomer.name}</div>
      <div className={styles.badgeRow}>
        <Badge
          appearance="filled"
          color={done ? 'success' : 'danger'}
          size="medium"
        >
          {done ? 'Take 5 ✓' : 'Take 5 Required'}
        </Badge>
        {done && job.completedAt && (
          <span style={{ fontSize: '11.5px', color: tokens.colorNeutralForeground3 }}>
            {formatCompletedDate(job.completedAt)}
          </span>
        )}
      </div>
    </Card>
  );
}
